import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../api/client";
import { replaceHashParams, useHashParams } from "../lib/useHashParams";
import type { ReactNode } from "react";

import {
  useClusters,
  useFindings,
  useMarkSeen,
  useRefreshFindingsAfterRun,
} from "../api/hooks";
import type { Finding, Profile } from "../api/types";
import { ChatPanel } from "../components/ChatPanel";
import { ClusterDrawer } from "../components/ClusterDrawer";
import { ClusterGraph } from "../components/ClusterGraph";
import { FindingDrawer } from "../components/FindingDrawer";
import { SplitPane } from "../components/SplitPane";
import {
  STATUS_META,
  fmtDate,
  fmtEURk,
  hostLabel,
  urgencyToken,
} from "../lib/format";

type Sort = "deadline" | "score";
type Tab = "liste" | "karte" | "programme";

/** The open tab lives in the address, after the view: `#/technologie?tab=liste`.
 *  A reload then lands where the reader was, and the link carries it.
 *
 *  Technology opens on the map: a hundred and eighty signals are a field
 *  before they are a list. Funding has no map and always opens on the list. */
function tabFromHash(type: "funding" | "technology"): Tab {
  const wanted = new URLSearchParams(location.hash.split("?")[1] ?? "").get("tab");
  if (type === "technology") return wanted === "liste" ? "liste" : "karte";
  // Funding opens on the programmes: a call is read in the context of the
  // programme it belongs to, and the deadlines are one tab away.
  return wanted === "liste" ? "liste" : "programme";
}

function writeTabToHash(tab: Tab, type: "funding" | "technology"): void {
  const isDefault = type === "technology" ? tab === "karte" : tab === "programme";
  replaceHashParams({ tab: isDefault ? null : tab });
}

function sourceHost(finding: Finding): string {
  return hostLabel(finding.url || finding.citations[0]?.url);
}

export function DbView({ type, profile }: { type: "funding" | "technology"; profile?: Profile }) {
  const { data: findings, isPending, isError, refetch } = useFindings(type, true);
  // Still needed for its effect: the list refetches when a run finishes.
  useRefreshFindingsAfterRun(profile?.slug);
  // Fixed per profile: a call is read by its deadline, a signal by its
  // score. The dropdown that offered three orders is gone.
  const sort: Sort = type === "funding" ? "deadline" : "score";
  const hashParams = useHashParams();
  const linkedId = hashParams.get("finding");
  const linkedProfile = hashParams.get("profile") || profile?.slug || "";
  const linkedTab = hashParams.get("tab");
  const [openId, setOpenIdState] = useState<string | null>(linkedId);
  const setOpenId = (id: string | null) => {
    setOpenIdState(id);
    replaceHashParams({ finding: id, profile: profile?.slug ?? null });
  };
  const [tab, setTabState] = useState<Tab>(() => tabFromHash(type));
  useEffect(() => {
    setOpenIdState(linkedId);
    setTabState(tabFromHash(type));
  }, [linkedId, linkedProfile, linkedTab, type]);
  const setTab = (next: Tab) => {
    setTabState(next);
    writeTabToHash(next, type);
  };
  const [minScore, setMinScore] = useState(0);
  useEffect(() => setMinScore(0), [profile?.id, type]);
  const [openCluster, setOpenCluster] = useState<string | null>(null);
  // Where an entry was opened from, so it can offer the way back.
  const [cameFrom, setCameFrom] = useState<string | null>(null);
  // Collapsed to start with: nine headings are an overview, a hundred and
  // eighty cards are not.
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const clusters = useClusters(type, type === "technology");
  const markSeen = useMarkSeen(type);

  const label = type === "funding" ? "Förderungen" : "Technologie";
  const cardFields = profile?.card_fields ?? ["trl", "source"];
  const fieldLabels = Object.fromEntries(
    (profile?.card_field_choices ?? []).map((choice) => [choice.key, choice.label]),
  );

  const all = useMemo(() => findings ?? [], [findings]);

  // Dashboard figures describe the whole inventory, not the current filter —
  // otherwise the numbers would move with every chip that is clicked.
  const stats = useMemo(() => {
    const countries = new Map<string, number>();
    const hosts = new Map<string, number>();
    for (const finding of all) {
      for (const country of finding.countries) {
        countries.set(country, (countries.get(country) ?? 0) + 1);
      }
      const host = sourceHost(finding);
      if (host && host !== "-") hosts.set(host, (hosts.get(host) ?? 0) + 1);
    }
    const byCount = (a: [string, number], b: [string, number]) => b[1] - a[1];
    return {
      total: all.length,
      countries: [...countries.entries()].sort(byCount),
      hosts: [...hosts.entries()].sort(byCount),
    };
  }, [all]);

  const list = useMemo(() => {
    const items = all.filter(f => type !== "funding" || minScore === 0 || (f.relevance_score != null && f.relevance_score >= minScore));
    if (sort === "score") {
      items.sort((a, b) => (b.relevance_score ?? -1) - (a.relevance_score ?? -1));
    } else {
      // By deadline: what is due soonest first, expired last, undated after.
      const rank = (f: Finding) =>
        f.days_left == null ? 9999 : f.days_left < 0 ? 9000 : f.days_left;
      items.sort((a, b) => rank(a) - rank(b));
    }
    return items;
  }, [all, sort, type, minScore]);

  /** The list, in sections — by cluster for technology, one nameless
   *  section otherwise. The clusters come in the order the API sorts them
   *  (largest first), so the sections match the map's sizes. */
  const grouped = useMemo<[string, Finding[]][]>(() => {
    // Funding on its programme tab: the programme is the order a funding
    // inventory already has, so no clustering is needed to find it.
    if (type === "funding") {
      if (tab !== "programme") return [["", list]];
      const byProgramme = new Map<string, Finding[]>();
      for (const entry of list) {
        const key = entry.programme?.trim() || "Ohne Programm";
        byProgramme.set(key, [...(byProgramme.get(key) ?? []), entry]);
      }
      return [...byProgramme.entries()].sort((one, two) => {
        // Largest first, but whatever has no programme goes last whatever
        // its size — it is a gap, not a group.
        if (one[0] === "Ohne Programm") return 1;
        if (two[0] === "Ohne Programm") return -1;
        return two[1].length - one[1].length;
      });
    }

    if (!(clusters.data ?? []).length) return [["", list]];
    const sections: [string, Finding[]][] = [];
    for (const cluster of clusters.data ?? []) {
      const entries = list.filter((entry) => entry.cluster_slug === cluster.slug);
      if (entries.length) sections.push([cluster.name, entries]);
    }
    const rest = list.filter(
      (entry) => !(clusters.data ?? []).some((cluster) => cluster.slug === entry.cluster_slug),
    );
    if (rest.length) sections.push(["Ohne Cluster", rest]);
    return sections;
  }, [list, clusters.data, type, tab]);

  const cachedFinding = linkedProfile === profile?.slug ? list.find((f) => f.id === openId) : null;
  const detail = useQuery<Finding>({
    queryKey: ["finding-detail", linkedProfile, openId],
    queryFn: () => apiGet(`/api/findings/detail?id=${encodeURIComponent(openId ?? "")}&profile=${encodeURIComponent(linkedProfile)}`),
    enabled: Boolean(openId) && !cachedFinding,
    retry: false,
  });
  const openFinding = cachedFinding ?? (detail.data?.type === type ? detail.data : null);

  return (
    <>
      <div className="view-head">
        <div>
          <h1>{label} · Datenbank</h1>
        </div>
      </div>

      <SplitPane
        storageKey={`db-split-half-${type}`}
        left={
          <>
            <div className="summary-bar">
              <Stat label={label} value={String(stats.total)} loading={isPending} />
              <Stat
                label="Länder"
                value={String(stats.countries.length)}
                loading={isPending}
              />
              <Stat label="Quellen" value={String(stats.hosts.length)} loading={isPending} />
            </div>

            <div className="toolbar view-tabs">
              <div className="tab-strip" role="tablist">
                {type === "funding" && (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "programme"}
                    className={"tab" + (tab === "programme" ? " on" : "")}
                    onClick={() => setTab("programme")}
                  >
                    Nach Programm
                  </button>
                )}
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "liste"}
                  className={"tab" + (tab === "liste" ? " on" : "")}
                  onClick={() => setTab("liste")}
                >
                  {type === "funding" ? "Nach Frist" : "Liste"}
                </button>
                {type === "funding" ? null : (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "karte"}
                    className={"tab" + (tab === "karte" ? " on" : "")}
                    onClick={() => setTab("karte")}
                  >
                    Karte
                  </button>
                )}
              </div>
            </div>

            {type === "funding" && <div className="score-filter">
              <div className="score-filter-heading">
                <label htmlFor="minimum-score">Filter Autoflug Score</label>
                <output htmlFor="minimum-score">{minScore === 0 ? "Alle" : "Ab " + minScore}</output>
              </div>
              <input id="minimum-score" type="range" min={0} max={100} step={1} value={minScore}
                aria-valuetext={minScore === 0 ? "Alle, auch unbewertete Treffer" : "Mindestens " + minScore + " von 100"}
                onChange={e => { setMinScore(Number(e.target.value)); setTab("liste"); }} />
              <div className="score-filter-footer">
                <span aria-live="polite">{list.length} von {all.length} Treffern</span>
                {minScore > 0 && <button className="btn secondary" onClick={() => setMinScore(0)}>Zurücksetzen</button>}
              </div>
            </div>}

            {tab === "karte" && type === "technology" && (
              <div className="db-graph">
                {clusters.isLoading && <div className="hint">Karte wird geladen…</div>}
                {!clusters.isLoading && (clusters.data ?? []).length === 0 && (
                  <div className="state-block">
                    <div className="glyph">◌</div>
                    <h3>Noch keine Cluster</h3>
                    <p>
                      Starten Sie einen Suchlauf, um die Karte zu erstellen.
                    </p>
                  </div>
                )}
                {(clusters.data ?? []).length > 0 && (
                  <ClusterGraph
                    findings={list}
                    clusters={clusters.data ?? []}
                    onOpen={(id) => {
                      setOpenId(id);
                      const hit = list.find((entry) => entry.id === id);
                      if (hit?.is_new) markSeen.mutate(id);
                    }}
                    onOpenCluster={setOpenCluster}
                  />
                )}
              </div>
            )}

            <div className="db-list" hidden={tab === "karte"}>
              {isPending && <Skeleton />}
              {isError && (
                <div className="state-block">
                  <div className="glyph">!</div>
                  <h3>Ansicht konnte nicht geladen werden</h3>
                  <button className="btn secondary" onClick={() => void refetch()}>
                    Erneut versuchen
                  </button>
                </div>
              )}
              {!isPending && !isError && list.length === 0 && (
                <div className="state-block">
                  <div className="glyph">∅</div>
                  <h3>{all.length ? "Keine Treffer für diesen Score" : "Keine Datensätze"}</h3>
                  <p>
                    {all.length ? "Score-Filter senken."
                      : "Starten Sie einen Suchlauf im Chat."}
                  </p>
                </div>
              )}
              {grouped.map(([heading, entries]) => {
                const isOpen = !heading || openGroups.includes(heading);
                return (
                <section className="list-group" key={heading}>
                  {heading && (
                    <button
                      type="button"
                      className={"list-group-head" + (isOpen ? " open" : "")}
                      aria-expanded={isOpen}
                      onClick={() =>
                        setOpenGroups((current) =>
                          current.includes(heading)
                            ? current.filter((name) => name !== heading)
                            : [...current, heading],
                        )
                      }
                    >
                      <span className="group-caret" aria-hidden="true">
                        ›
                      </span>
                      {heading}
                      <span className="chip-count">{entries.length}</span>
                    </button>
                  )}
                  {isOpen &&
                    entries.map((finding) => (
                    <RecCard
                      key={finding.id}
                      finding={finding}
                      cardFields={cardFields}
                      fieldLabels={fieldLabels}
                      onOpen={() => {
                        setOpenId(finding.id);
                        if (finding.is_new) markSeen.mutate(finding.id);
                      }}
                    />
                    ))}
                </section>
                );
              })}
            </div>

          </>
        }
        right={profile ? <ChatPanel profile={profile} embedded /> : null}
      />

      <ClusterDrawer
        cluster={(clusters.data ?? []).find((entry) => entry.slug === openCluster) ?? null}
        entries={list.filter((entry) => entry.cluster_slug === openCluster)}
        onClose={() => setOpenCluster(null)}
        onOpenEntry={(id) => {
          setCameFrom(openCluster);
          setOpenCluster(null);
          setOpenId(id);
        }}
      />
      {openId && detail.isError && <div role="alert" className="hint">
        Dieser Eintrag ist nicht mehr verfügbar.
        <button className="btn secondary" onClick={() => setOpenId(null)}>Schließen</button>
      </div>}
      <FindingDrawer
        finding={openFinding}
        onClose={() => {
          setOpenId(null);
          setCameFrom(null);
        }}
        onBack={
          cameFrom
            ? () => {
                setOpenId(null);
                setOpenCluster(cameFrom);
                setCameFrom(null);
              }
            : undefined
        }
      />
    </>
  );
}

/**
 * One figure of the inventory.
 *
 * The subtitle used to spell the largest few out ("DE 11 · EU 5 · …"), which
 * is a second, worse version of the list underneath. While the figure is
 * still being fetched a bar stands in its place, so the tile keeps its height
 * and the page does not jump when the number arrives.
 */
function Stat({ label, value, loading }: { label: string; value: string; loading?: boolean }) {
  return (
    <div className="est">
      <span className="fact-label">{label}</span>
      {loading ? (
        <span className="skeleton stat-skeleton" />
      ) : (
        <span className="stat-value">{value}</span>
      )}
    </div>
  );
}

export function SourceLink({ finding }: { finding: Finding }) {
  const href = finding.url || finding.citations[0]?.url;
  if (!href) return <>-</>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="source-link"
      title={href}
      onClick={(event) => event.stopPropagation()}
    >
      {hostLabel(href)} ↗
    </a>
  );
}

function fieldValue(finding: Finding, key: string): ReactNode {
  switch (key) {
    case "budget_total_eur":
      return finding.budget_total_eur != null ? fmtEURk(finding.budget_total_eur) : "-";
    case "trl": {
      const trl = finding.maturity_trl ?? finding.trl_min;
      if (trl == null) return "-";
      return finding.trl_max != null && finding.trl_max !== trl
        ? `${trl}–${finding.trl_max}`
        : String(trl);
    }
    case "source":
      return <SourceLink finding={finding} />;
    case "deadline":
      return finding.deadline ? fmtDate(finding.deadline) : "-";
    case "status":
      return finding.status ? (STATUS_META[finding.status]?.label ?? finding.status) : "-";
    case "programme":
      return finding.programme ?? "-";
    case "organisation":
      return finding.organisation ?? "-";
    case "call_identifier":
      return finding.call_identifier ?? "-";
    case "technology_field":
      return finding.technology_field ?? "-";
    case "relevance_score":
      return finding.relevance_score != null ? String(finding.relevance_score) : "-";
    case "funding_rate":
      return finding.funding_rate != null ? `${Math.round(finding.funding_rate * 100)}%` : "-";
    case "countries":
      return finding.countries.join(", ") || "-";
    case "first_seen":
      return fmtDate(finding.first_seen);
    case "last_changed":
      return fmtDate(finding.last_changed);
    case "confidence":
      return finding.confidence ?? "-";
    default:
      return "-";
  }
}

function RecCard({
  finding,
  cardFields,
  fieldLabels,
  onOpen,
}: {
  finding: Finding;
  cardFields: string[];
  fieldLabels: Record<string, string>;
  onOpen: () => void;
}) {
  const token = urgencyToken(finding.days_left);
  return (
    <div
      className="rec-card"
      data-changed={finding.changes.length > 0 ? "true" : undefined}
      style={{ ["--rec-urgency" as string]: `var(${token})` }}
      onClick={onOpen}
    >
      <div className="rec-head">
        {finding.is_new && <span className="new-chip">NEU</span>}
        <span className="rec-title">{finding.title}</span>
        {finding.days_left != null && finding.days_left >= 0 && (
          <span
            className="days-chip"
            style={{
              background: `color-mix(in oklab, var(${token}) 22%, transparent)`,
              color: `var(${token})`,
            }}
          >
            {finding.days_left} Tage
          </span>
        )}
      </div>
      <div className="rec-facts">
        {finding.type === "funding" && <div className="fact funding-score">
          <span className="fact-label">Autoflug Score</span>
          <span className="fact-value" title={finding.relevance_rationale || undefined}>
            {finding.relevance_score != null ? finding.relevance_score + " / 100" : "Bewertung ausstehend"}
          </span>
        </div>}
        {cardFields.filter(key => finding.type !== "funding" || !["trl", "relevance_score"].includes(key)).map((key) => (
          <div className="fact" key={key}>
            <span className="fact-label">{fieldLabels[key] ?? key}</span>
            <span className="fact-value">{fieldValue(finding, key)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Skeleton() {
  return (
    <>
      <div className="skeleton-row">
        <div className="skeleton" style={{ width: "30%" }} />
        <div className="skeleton" style={{ width: "20%" }} />
        <div className="skeleton" style={{ width: "15%" }} />
      </div>
      <div className="skeleton-row">
        <div className="skeleton" style={{ width: "40%" }} />
        <div className="skeleton" style={{ width: "25%" }} />
      </div>
      <div className="skeleton-row">
        <div className="skeleton" style={{ width: "60%" }} />
      </div>
    </>
  );
}
